import type React from "react";
import { TableRow, TableCell, Box, Typography, IconButton, Button, Collapse } from "@mui/material";
import { Add as AddIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import type { TranslationKeys } from "./utils";
import TruckHistoryTable from "./TruckHistoryTable";
import type { Truck } from "../../../types/truck";
import type { TruckHistory } from "../../../types/truckHistory";
import type { AppDispatch, RootState } from "../../../store";
import { fetchTruckHistories } from "../../../store/features/truckHistorySlice";
import type { PaginationResponse } from "../../../types/paginationResponse";
import { isUserAdmin } from "../../../types/user";

interface TruckRowExpandedProps {
  truck: Truck;
  isExpanded: boolean;
  history: TruckHistory[];
  loading: boolean;
  getCityName: (cityId: string) => string;
  onAddHistory: (truckId: string) => void;
  pagination?: PaginationResponse;
}

const TruckRowExpanded: React.FC<TruckRowExpandedProps> = ({
  truck,
  isExpanded,
  history,
  loading,
  getCityName,
  onAddHistory,
  pagination,
}) => {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch<AppDispatch>();

  const handleRefresh = () => {
    dispatch(
      fetchTruckHistories({
        truckId: truck._id,
        page: 1,
        pageSize: pagination?.pageSize || 10,
      }),
    );
  };

  return (
    <TableRow
      sx={{
        "&:hover": {
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(10, 10, 10, 0.1) !important"
              : "rgba(10, 10, 10, 0.02) !important",
          cursor: "pointer",
          "& .MuiTableCell-root": {
            backgroundColor: "inherit !important",
          },
        },
        ...(isExpanded && {
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(10, 10, 10, 0.1) !important"
              : "rgba(10, 10, 10, 0.02) !important",
          "& .MuiTableCell-root": {
            backgroundColor: "inherit !important",
          },
        }),
      }}
    >
      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={isUserAdmin(user) ? 8 : 7}>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ margin: 2, overflowX: "auto" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" component="div">
                  {t("Truck History" as TranslationKeys)}
                </Typography>
                <IconButton
                  onClick={handleRefresh}
                  disabled={loading}
                  size="small"
                  sx={{
                    padding: "4px",
                    marginTop: "-2px",
                    "& .MuiSvgIcon-root": {
                      fontSize: "1rem",
                    },
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={() => onAddHistory(truck._id)}
                startIcon={<AddIcon />}
              >
                {t("Add history" as TranslationKeys)}
              </Button>
            </Box>
            <TruckHistoryTable
              history={history}
              loading={loading}
              getCityName={getCityName}
              truckId={truck._id}
              isExpanded={isExpanded}
              pagination={pagination}
            />
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
};

export default TruckRowExpanded;
